// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {useMergeRefs} from '@app/features/app/hooks/useMergeRefs';
import * as ReactionCommands from '@app/features/messaging/commands/ReactionCommands';
import {MessageReactionsModal} from '@app/features/messaging/components/modals/MessageReactionsModal';
import {useReactionTooltip} from '@app/features/messaging/hooks/useReactionTooltip';
import type {Message} from '@app/features/messaging/models/Message';
import MessageReactions from '@app/features/messaging/state/MessageReactions';
import {getReactionTooltip, useEmojiURL} from '@app/features/messaging/utils/ReactionUtils';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {EmojiTooltipContent} from '@app/features/ui/emoji_tooltip_content/EmojiTooltipContent';
import {useTooltipPortalRoot} from '@app/features/ui/tooltip/Tooltip';
import {getReducedMotionProps, TOOLTIP_MOTION} from '@app/features/ui/utils/ReducedMotionAnimation';
import type {MessageReaction} from '@app/vendor/schema/domains/message/MessageResponseSchemas';
import {FloatingPortal} from '@floating-ui/react';
import {useLingui} from '@lingui/react/macro';
import {AnimatePresence, motion} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import React, {useEffect, useMemo, useRef} from 'react';
export const ReactionTooltip = observer(
	({
		message,
		reaction,
		children,
		hoveredEmojiUrl,
		animationSyncKey,
		onRequestAnimationSync,
		onTooltipHoverChange,
	}: {
		message: Message;
		reaction: MessageReaction;
		children: React.ReactElement<Record<string, unknown> & {ref?: React.Ref<HTMLElement>}>;
		hoveredEmojiUrl?: string | null;
		animationSyncKey?: number;
		onRequestAnimationSync?: () => void;
		onTooltipHoverChange?: (hovering: boolean) => void;
	}) => {
		const {t} = useLingui();
		const tooltipPortalRoot = useTooltipPortalRoot();
		const tooltipMotion = getReducedMotionProps(TOOLTIP_MOTION, Accessibility.useReducedMotion);
		const {targetRef, tooltipRef, state, updatePosition, handlers, tooltipHandlers, hide} = useReactionTooltip(500);
		const prevIsOpenRef = useRef(false);
		useEffect(() => {
			if (state.isOpen && !prevIsOpenRef.current) {
				onRequestAnimationSync?.();
			}
			prevIsOpenRef.current = state.isOpen;
		}, [state.isOpen, onRequestAnimationSync]);
		useEffect(() => {
			if (!state.isOpen) {
				onTooltipHoverChange?.(false);
			}
		}, [state.isOpen, onTooltipHoverChange]);
		const fetchStatus = MessageReactions.getFetchStatus(message.id, reaction.emoji);
		const isLoading = fetchStatus === 'pending';
		const tooltipText = getReactionTooltip(message, reaction.emoji);
		const emojiIdentifier = reaction.emoji.id ?? reaction.emoji.name;
		const tooltipEmojiKey = `${emojiIdentifier}-${animationSyncKey ?? 0}`;
		const fallbackEmojiUrl = useEmojiURL({
			emoji: reaction.emoji,
			isHovering: state.isOpen,
			forceAnimate: state.isOpen,
		});
		const emojiUrl = hoveredEmojiUrl ?? fallbackEmojiUrl;
		useEffect(() => {
			if (!state.isOpen || fetchStatus === 'pending' || fetchStatus === 'error') {
				return;
			}
			if (fetchStatus === 'idle') {
				ReactionCommands.getReactions(message.channelId, message.id, reaction.emoji, {
					limit: 3,
					totalCount: reaction.count,
				}).catch((_error) => {});
			}
		}, [state.isOpen, message.channelId, message.id, reaction.emoji, fetchStatus]);
		const handleClick = () => {
			hide();
			ModalCommands.push(
				modal(() => (
					<MessageReactionsModal channelId={message.channelId} messageId={message.id} openToReaction={reaction} />
				)),
			);
		};
		const tooltipMouseHandlers = useMemo(
			() => ({
				...tooltipHandlers,
				onMouseEnter(event: React.MouseEvent) {
					tooltipHandlers.onMouseEnter(event);
					onTooltipHoverChange?.(true);
				},
				onMouseLeave(event: React.MouseEvent) {
					tooltipHandlers.onMouseLeave(event);
					onTooltipHoverChange?.(false);
				},
			}),
			[tooltipHandlers, onTooltipHoverChange],
		);
		const child = React.Children.only(children);
		const childRef = child.props.ref ?? null;
		const mergedRef = useMergeRefs([targetRef, childRef]);
		return (
			<>
				{React.cloneElement(child, {
					ref: mergedRef,
					...handlers,
				})}
				{state.isOpen && (
					<FloatingPortal root={tooltipPortalRoot}>
						<AnimatePresence>
							<motion.div
								ref={(node: HTMLDivElement | null) => {
									(tooltipRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
									if (node && targetRef.current) {
										updatePosition();
									}
								}}
								style={{
									position: 'fixed',
									left: state.x,
									top: state.y,
									zIndex: 'var(--z-index-tooltip)',
									visibility: state.isReady ? 'visible' : 'hidden',
								}}
								{...tooltipMotion}
								{...tooltipMouseHandlers}
							>
								<EmojiTooltipContent
									emojiUrl={emojiUrl}
									emojiAlt={reaction.emoji.name}
									emojiKey={tooltipEmojiKey}
									primaryContent={tooltipText}
									subtext={t`Click to view all reactions`}
									isLoading={isLoading}
									interactive
									onClick={handleClick}
								/>
							</motion.div>
						</AnimatePresence>
					</FloatingPortal>
				)}
			</>
		);
	},
);
